import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from './authFetch';
import { UserContext } from './App';
import {
  Bell, Clock, ChevronDown, ChevronRight, Calendar,
  CheckCircle, XCircle, User, Phone, Mail, AlertTriangle,
  Plus, Loader2
} from 'lucide-react';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

// =====================================================
// Aging-In Tab
// =====================================================
function AgingInTab() {
  const navigate = useNavigate();
  const [agingData, setAgingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedMonths, setExpandedMonths] = useState({});
  const [reminderForm, setReminderForm] = useState(null); // { customerId, reason, follow_up_date }
  const [submitting, setSubmitting] = useState(false);

  const fetchAgingData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authFetch(`${BASE_URL}/api/dashboard/aging-in/`);
      if (!response.ok) {
        throw new Error(`Failed to fetch aging-in data: ${response.status} ${response.statusText}`);
      }
      const data = await response.json();
      setAgingData(data);

      // Auto-expand the first month with customers
      if (data.months && data.months.length > 0) {
        const firstMonth = data.months.find(m => m.count > 0);
        if (firstMonth) {
          setExpandedMonths({ [firstMonth.month]: true });
        }
      }
    } catch (err) {
      console.error('Error fetching aging-in data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAgingData();
  }, [fetchAgingData]);

  const toggleMonth = (month) => {
    setExpandedMonths(prev => ({
      ...prev,
      [month]: !prev[month]
    }));
  };

  const getUrgencyColor = (daysUntil65) => {
    if (daysUntil65 < 30) return { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', badge: 'bg-red-100 text-red-800' };
    if (daysUntil65 < 90) return { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', badge: 'bg-orange-100 text-orange-800' };
    if (daysUntil65 < 180) return { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', badge: 'bg-yellow-100 text-yellow-800' };
    return { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', badge: 'bg-green-100 text-green-800' };
  };

  const openReminderForm = (customerId) => {
    setReminderForm({
      customerId,
      reason: '',
      follow_up_date: ''
    });
  };

  const closeReminderForm = () => {
    setReminderForm(null);
  };

  const handleSetReminder = async (e) => {
    e.preventDefault();
    if (!reminderForm || !reminderForm.reason.trim() || !reminderForm.follow_up_date) return;

    try {
      setSubmitting(true);
      const response = await authFetch(`${BASE_URL}/api/reminders/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer: reminderForm.customerId,
          reason: reminderForm.reason.trim(),
          follow_up_date: reminderForm.follow_up_date
        })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `Failed to create reminder: ${response.status}`);
      }

      // Refresh data to show updated has_reminder status
      await fetchAgingData();
      setReminderForm(null);
    } catch (err) {
      console.error('Error creating reminder:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500 mr-2" />
        <span className="text-gray-500">Loading aging-in data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertTriangle className="w-5 h-5 text-red-500 mr-2" />
          <span className="text-red-700">{error}</span>
        </div>
        <button
          onClick={fetchAgingData}
          className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!agingData || !agingData.months || agingData.months.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p className="text-lg font-medium">No aging-in customers found</p>
        <p className="text-sm mt-1">Customers approaching age 65 will appear here.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-center">
          <Clock className="w-5 h-5 text-blue-600 mr-2" />
          <span className="text-blue-800 font-medium">
            {agingData.total_count} customer{agingData.total_count !== 1 ? 's' : ''} approaching age 65
          </span>
        </div>
      </div>

      {/* Month Accordions */}
      <div className="space-y-3">
        {agingData.months.map((monthData) => {
          const isExpanded = expandedMonths[monthData.month];
          return (
            <div key={monthData.month} className="border rounded-lg overflow-hidden">
              {/* Month Header */}
              <button
                onClick={() => toggleMonth(monthData.month)}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center">
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 text-gray-500 mr-2" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-500 mr-2" />
                  )}
                  <span className="font-medium text-gray-800">{monthData.label}</span>
                </div>
                <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2.5 py-0.5 rounded-full">
                  {monthData.count}
                </span>
              </button>

              {/* Month Content */}
              {isExpanded && (
                <div className="divide-y">
                  {monthData.customers.map((customer) => {
                    const colors = getUrgencyColor(customer.days_until_65);
                    const isReminderOpen = reminderForm && reminderForm.customerId === customer.id;

                    return (
                      <div key={customer.id} className={`p-4 ${colors.bg}`}>
                        <div className="flex items-start justify-between">
                          {/* Customer Info */}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <button
                                onClick={() => navigate(`/customers/${customer.id}`)}
                                className="text-blue-600 hover:text-blue-800 font-medium hover:underline"
                              >
                                {customer.first_name} {customer.last_name}
                              </button>
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${colors.badge}`}>
                                {customer.days_until_65} day{customer.days_until_65 !== 1 ? 's' : ''}
                              </span>
                              {customer.has_reminder && (
                                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 flex items-center gap-1">
                                  <Bell className="w-3 h-3" />
                                  Reminder Set
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-600 space-y-0.5">
                              <div className="flex items-center gap-4">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3.5 h-3.5" />
                                  DOB: {customer.dob}
                                </span>
                                <span className={`font-medium ${colors.text}`}>
                                  Turns 65 on {customer.turns_65_on}
                                </span>
                              </div>
                              <div className="flex items-center gap-4">
                                {customer.phone && (
                                  <span className="flex items-center gap-1">
                                    <Phone className="w-3.5 h-3.5" />
                                    {customer.phone}
                                  </span>
                                )}
                                {customer.email && (
                                  <span className="flex items-center gap-1">
                                    <Mail className="w-3.5 h-3.5" />
                                    {customer.email}
                                  </span>
                                )}
                              </div>
                              {customer.status && (
                                <span className="text-xs text-gray-500">Status: {customer.status}</span>
                              )}
                            </div>
                          </div>

                          {/* Set Reminder Button */}
                          <div className="ml-4 flex-shrink-0">
                            {!isReminderOpen && (
                              <button
                                onClick={() => openReminderForm(customer.id)}
                                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
                              >
                                <Plus className="w-4 h-4" />
                                Set Reminder
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Inline Reminder Form */}
                        {isReminderOpen && (
                          <form onSubmit={handleSetReminder} className="mt-3 bg-white border rounded-lg p-3">
                            <div className="space-y-3">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Reason
                                </label>
                                <textarea
                                  value={reminderForm.reason}
                                  onChange={(e) => setReminderForm(prev => ({ ...prev, reason: e.target.value }))}
                                  placeholder="e.g., Medicare enrollment window approaching - schedule consultation"
                                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  rows={2}
                                  required
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Follow-up Date
                                </label>
                                <input
                                  type="date"
                                  value={reminderForm.follow_up_date}
                                  onChange={(e) => setReminderForm(prev => ({ ...prev, follow_up_date: e.target.value }))}
                                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  required
                                />
                              </div>
                              <div className="flex gap-2">
                                <button
                                  type="submit"
                                  disabled={submitting}
                                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                                >
                                  {submitting ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Bell className="w-4 h-4" />
                                  )}
                                  Save Reminder
                                </button>
                                <button
                                  type="button"
                                  onClick={closeReminderForm}
                                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-600"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </form>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// =====================================================
// Reminders Tab
// =====================================================
function RemindersTab() {
  const navigate = useNavigate();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending'); // 'pending' or 'all'
  const [actionLoading, setActionLoading] = useState(null); // reminder id being acted on

  const fetchReminders = useCallback(async (filter) => {
    try {
      setLoading(true);
      setError(null);
      const url = filter === 'all'
        ? `${BASE_URL}/api/reminders/?status=all`
        : `${BASE_URL}/api/reminders/?status=pending`;
      const response = await authFetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch reminders: ${response.status} ${response.statusText}`);
      }
      const data = await response.json();
      // Handle both array response and paginated response
      const results = Array.isArray(data) ? data : (data.results || []);
      // Sort by follow_up_date ascending
      results.sort((a, b) => new Date(a.follow_up_date) - new Date(b.follow_up_date));
      setReminders(results);
    } catch (err) {
      console.error('Error fetching reminders:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReminders(statusFilter);
  }, [fetchReminders, statusFilter]);

  const handleUpdateStatus = async (reminderId, newStatus) => {
    try {
      setActionLoading(reminderId);
      const response = await authFetch(`${BASE_URL}/api/reminders/${reminderId}/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        throw new Error(`Failed to update reminder: ${response.status}`);
      }

      // Refresh the list
      await fetchReminders(statusFilter);
    } catch (err) {
      console.error('Error updating reminder:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const getDateColor = (dateStr, status) => {
    if (status === 'completed') return 'text-green-600';
    if (status === 'dismissed') return 'text-gray-400';

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const followUp = new Date(dateStr + 'T00:00:00');

    if (followUp < today) return 'text-red-600 font-semibold';
    if (followUp.getTime() === today.getTime()) return 'text-orange-600 font-semibold';
    return 'text-gray-700';
  };

  const getRowStyle = (dateStr, status) => {
    if (status === 'completed') return 'bg-green-50 border-green-100';
    if (status === 'dismissed') return 'bg-gray-50 border-gray-100 opacity-60';

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const followUp = new Date(dateStr + 'T00:00:00');

    if (followUp < today) return 'bg-red-50 border-red-200';
    if (followUp.getTime() === today.getTime()) return 'bg-orange-50 border-orange-200';
    return 'bg-white border-gray-200';
  };

  const getDateLabel = (dateStr) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const followUp = new Date(dateStr + 'T00:00:00');
    const diffDays = Math.round((followUp - today) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return `${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''} overdue`;
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    return `In ${diffDays} days`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return (
          <span className="flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3" />
            Completed
          </span>
        );
      case 'dismissed':
        return (
          <span className="flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
            <XCircle className="w-3 h-3" />
            Dismissed
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500 mr-2" />
        <span className="text-gray-500">Loading reminders...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertTriangle className="w-5 h-5 text-red-500 mr-2" />
          <span className="text-red-700">{error}</span>
        </div>
        <button
          onClick={() => fetchReminders(statusFilter)}
          className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Status Filter */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-sm text-gray-600 font-medium">Show:</span>
        <button
          onClick={() => setStatusFilter('pending')}
          className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
            statusFilter === 'pending'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Pending
        </button>
        <button
          onClick={() => setStatusFilter('all')}
          className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
            statusFilter === 'all'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          All (incl. completed)
        </button>
      </div>

      {/* Reminders List */}
      {reminders.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-lg font-medium">No reminders found</p>
          <p className="text-sm mt-1">
            {statusFilter === 'pending'
              ? 'You have no pending reminders. Set one from the Aging-In tab.'
              : 'No reminders have been created yet.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {reminders.map((reminder) => (
            <div
              key={reminder.id}
              className={`border rounded-lg p-4 transition-colors ${getRowStyle(reminder.follow_up_date, reminder.status)}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {/* Customer Name + Status Badge */}
                  <div className="flex items-center gap-2 mb-1">
                    <User className="w-4 h-4 text-gray-400" />
                    <button
                      onClick={() => navigate(`/customers/${reminder.customer}`)}
                      className="text-blue-600 hover:text-blue-800 font-medium hover:underline"
                    >
                      {reminder.customer_name}
                    </button>
                    {getStatusBadge(reminder.status)}
                  </div>

                  {/* Reason */}
                  <p className="text-sm text-gray-700 ml-6 mb-1">{reminder.reason}</p>

                  {/* Date Info */}
                  <div className="flex items-center gap-3 ml-6">
                    <span className={`text-sm flex items-center gap-1 ${getDateColor(reminder.follow_up_date, reminder.status)}`}>
                      <Calendar className="w-3.5 h-3.5" />
                      {reminder.follow_up_date}
                    </span>
                    {reminder.status === 'pending' && (
                      <span className={`text-xs ${getDateColor(reminder.follow_up_date, reminder.status)}`}>
                        ({getDateLabel(reminder.follow_up_date)})
                      </span>
                    )}
                    {reminder.created_at && (
                      <span className="text-xs text-gray-400">
                        Created: {new Date(reminder.created_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                {reminder.status === 'pending' && (
                  <div className="ml-4 flex-shrink-0 flex gap-2">
                    <button
                      onClick={() => handleUpdateStatus(reminder.id, 'completed')}
                      disabled={actionLoading === reminder.id}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                    >
                      {actionLoading === reminder.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <CheckCircle className="w-4 h-4" />
                      )}
                      Complete
                    </button>
                    <button
                      onClick={() => handleUpdateStatus(reminder.id, 'dismissed')}
                      disabled={actionLoading === reminder.id}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-100 disabled:opacity-50 transition-colors"
                    >
                      <XCircle className="w-4 h-4" />
                      Dismiss
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =====================================================
// Main Component
// =====================================================
export default function AgentAlerts() {
  const { currentUser } = useContext(UserContext) || { currentUser: null };
  const [activeTab, setActiveTab] = useState('aging-in');

  const TABS = [
    { id: 'aging-in', label: 'Aging-In', icon: Clock },
    { id: 'reminders', label: 'Reminders', icon: Bell },
  ];

  return (
    <div className="p-6 max-w-7xl">
      <div className="flex items-center gap-3 mb-4">
        <AlertTriangle className="w-6 h-6 text-amber-500" />
        <h1 className="text-2xl font-semibold">Agent Alerts</h1>
      </div>

      {/* Tab Navigation */}
      <div className="border-b mb-6">
        <div className="flex space-x-1">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600 bg-blue-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'aging-in' && <AgingInTab />}
      {activeTab === 'reminders' && <RemindersTab />}
    </div>
  );
}
